import {
  Alert,
  Box,
  CircularProgress,
  Divider,
  Typography,
  useTheme,
} from "@mui/material";
import { useParams, Link, useSearchParams } from "react-router";

import { ActionDetails, ActionLink } from "@/Actions/components/action";
import { Invocations } from "@/Actions/components/Invocations";
import { useAuth } from "@/providers";
import {
  PageBreadcrumbs,
  PageLayout,
  RobotoStyledTab,
  RobotoStyledTabs,
  RobotoTabPanel,
} from "@/shared/components";
import { useAction } from "@/shared/domain/actions/hooks";
import { useRegisterViewingContext } from "@/shared/services/ViewingContextService";

/**
 * Details page for one action, at the URL `Goto.createActionRoute` builds. The
 * path names the action, after its owning org when the link names one; with no
 * owner, the page reads the action from the viewer's current org. A non-empty
 * `digest` query parameter pins the page to that version; without one the page
 * shows the latest version.
 */
export function Action() {
  const { owner, name } = useParams<{ owner?: string; name: string }>();

  const [searchParams, setSearchParams] = useSearchParams();
  const digest = searchParams.get("digest") || undefined;

  const tabSearchParam = searchParams.get("tab");
  const tabValue = tabSearchParam !== null ? parseInt(tabSearchParam) : 0;

  const theme = useTheme();
  const { currentOrganization } = useAuth();

  const actionQuery = useAction(name, digest, {
    resourceOwnerId: owner ?? currentOrganization?.org_id,
  });

  const actionRecord = actionQuery.data;

  useRegisterViewingContext("action", { orgId: actionRecord?.org_id });

  // The viewer's org can edit only its action's latest version, and only a page
  // without a digest is known to show that version: a digest may name the
  // latest version too, but the page has no way to tell.
  const editable =
    actionRecord?.org_id === currentOrganization?.org_id &&
    digest === undefined;

  const breadcrumbs = [
    <Link key="list" to="/actions">
      Actions
    </Link>,
    <ActionLink
      key="latest"
      action={name === undefined ? null : { name, owner }}
      linkSx={{
        fontSize: "0.95rem",
        fontWeight: "500",
        color: theme.palette.text.primary,
      }}
    />,
  ];
  if (digest !== undefined) {
    breadcrumbs.push(
      <Typography
        key="digest"
        sx={{ fontSize: "0.95rem", fontWeight: "500" }}
        color="text.secondary"
      >
        {digest}
      </Typography>,
    );
  }

  const Breadcrumbs = <PageBreadcrumbs>{breadcrumbs}</PageBreadcrumbs>;

  if (actionQuery.isLoading || (!actionRecord && !actionQuery.isError)) {
    return (
      <PageLayout title={"Roboto - Action"} headerContentLeft={Breadcrumbs}>
        <CircularProgress />
      </PageLayout>
    );
  }

  let errorContent = null;
  let pageContent = null;
  if (actionQuery.isError) {
    errorContent = (
      <Box
        sx={{
          mt: theme.spacing(2),
          mb: theme.spacing(1),
        }}
      >
        <Alert severity="error">{actionQuery.error.message}</Alert>
      </Box>
    );
  } else if (!actionRecord) {
    errorContent = (
      <Box
        sx={{
          mt: theme.spacing(2),
          mb: theme.spacing(1),
        }}
      >
        <Alert severity="error">
          Failed to load action &apos;{name}&apos;.
          <br />
          Try refreshing the page.
        </Alert>
      </Box>
    );
  } else {
    pageContent = (
      <Box
        sx={{
          padding: theme.spacing(1, 0),
          paddingBottom: theme.spacing(1),
        }}
      >
        <RobotoTabPanel index={0} value={tabValue}>
          <ActionDetails
            action={actionRecord}
            currentOrg={currentOrganization}
            canEdit={editable}
          />
        </RobotoTabPanel>

        <RobotoTabPanel
          index={1}
          value={tabValue}
          sx={{
            paddingTop: theme.spacing(1),
            paddingBottom: theme.spacing(1),
          }}
        >
          <Invocations
            action={actionRecord ?? undefined}
            currentOrg={currentOrganization}
          />
        </RobotoTabPanel>
      </Box>
    );
  }

  return (
    <PageLayout title={"Roboto - Action"} headerContentLeft={Breadcrumbs}>
      {errorContent}

      {!actionQuery.isError && (
        <Box
          sx={{
            paddingTop: theme.spacing(0.5),
          }}
        >
          <RobotoStyledTabs
            value={tabValue}
            onChange={(_event, newValue) => {
              // The tab replaces the whole query, so switching tabs on a
              // pinned version moves the page to the latest version.
              setSearchParams({ tab: String(newValue) });
            }}
            aria-label="actions tabs"
          >
            <RobotoStyledTab label="Details" />
            <RobotoStyledTab label="Invocations" />
          </RobotoStyledTabs>

          <Divider
            sx={{
              borderColor: theme.border.color,
            }}
          />
        </Box>
      )}

      {pageContent}
    </PageLayout>
  );
}
